import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateEx } from '../../../functions/date-ex';
import { numericAdaOrLeaveKeyFromClaim } from '../../../functions/todo-claim-numeric-key';
import { TaskRequest } from '../../../models/api/todo/task-request';
import { TaskResponse } from '../../../models/api/todo/task-response';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ToggleSwitchComponent } from '../../core/controls/toggle-switch.component';
import { TagComponent } from '../../core/labels/tag.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';

@Component({
  selector: 'app-task-week',
  imports: [
    TagComponent,
    CommonModule,
    ButtonComponent,
    ToggleSwitchComponent,
    LoadingPanelComponent,
  ],
  template: `
    <div class="tw-flex tw-items-center tw-justify-center tw-flex-col">
      <div class="tw-flex tw-flex-row tw-flex-wrap tw-w-full tw-pb-1 tw-gap-2">
        <div
          class="tw-flex tw-flex-wrap tw-items-center tw-justify-center sm:tw-justify-between tw-w-full tw-gap-2"
        >
          <div
            class="tw-flex tw-justify-center sm:tw-justify-start tw-w-full sm:tw-w-[200px]"
          >
            <app-button
              classes="calendar-button calendar-today"
              label="Today"
              ariaLabel="Today"
              title="Today"
              (click)="goToToday()"
            />
          </div>
          <span
            class="tw-inline-flex tw-items-center tw-justify-center tw-w-full sm:tw-w-auto"
          >
            <app-button
              icon="fa-light fa-chevron-left"
              classes="calendar-button calendar-left"
              label=""
              ariaLabel="Previous week"
              title="Previous week"
              (click)="goToPreviousWeek()"
            />
            <b class="date-span">{{ formattedWeekRange }}</b>
            <app-button
              icon="fa-light fa-chevron-right"
              classes="calendar-button calendar-right"
              label=""
              ariaLabel="Next week"
              title="Next week"
              (click)="goToNextWeek()"
            />
          </span>
          <div
            class="tw-flex tw-justify-center sm:tw-justify-end tw-w-full sm:tw-w-[200px]"
          >
            <app-toggle-switch
              [control]="control"
              label="Include completed"
              [showHelpText]="false"
            />
          </div>
        </div>
      </div>
      <app-loading-panel
        loadingStyle="Box"
        [loadingHeight]="150"
        [state]="loading ? 'Loading' : 'Loaded'"
        class="tw-w-full"
      >
        <ng-template #loaded>
          <div
            class="tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 md:tw-flex md:tw-flex-row tw-gap-2 md:tw-gap-0 tw-w-full"
          >
            @for (item of items; track item.day) {
              <div
                class="tw-rounded-lg tw-border tw-border-[var(--sed-grey-60-color)] tw-cursor-pointer tw-bg-white hover:tw-drop-shadow-[0_4px_4px_var(--sed-grey-100-color)] md:tw-flex-1 md:tw-grow md:tw-m-1 focus:tw-outline focus:tw-outline-2 focus:tw-outline-[var(--sed-engaged-black-color)] focus:tw-outline-offset-[3px]"
                role="button"
                tabindex="0"
                [attr.aria-label]="item.day + ', ' + item.displayDate"
                [attr.aria-pressed]="item.active"
                (click)="calendarOnClick(item)"
                (keydown.enter)="calendarOnClick(item)"
                (keydown.space)="calendarOnClick(item); $event.preventDefault()"
                [ngClass]="{
                  'tw-drop-shadow-[0_4px_4px_var(--sed-grey-60-color)]':
                    item.active,
                }"
              >
                <div class="tw-flex tw-flex-col tw-h-40">
                  <div
                    class="tw-text-center tw-font-bold tw-py-0.5 tw-mb-2 tw-bg-[var(--sed-open-grey-color)] tw-rounded-t-lg tw-border-b tw-border-[var(--sed-grey-60-color)] week-header"
                  >
                    {{ item.day }}, {{ item.displayDate }}
                  </div>

                  @if (item.completed) {
                    <div class="tw-flex tw-items-center tw-pb-0.5">
                      <app-tag
                        tagStyle="Left"
                        type="Success"
                        class="tw-pl-1"
                        width="30px"
                      >
                        {{ item.completed }}</app-tag
                      >
                      <span class="tw-font-boldx tw-px-1.5">Completed</span>
                    </div>
                  }
                  @if (item.pastDue) {
                    <div class="tw-flex tw-items-center tw-pb-0.5">
                      <app-tag
                        tagStyle="Left"
                        type="Error"
                        class="tw-pl-1"
                        width="30px"
                      >
                        {{ item.pastDue }}</app-tag
                      >
                      <span class="tw-font-boldx tw-px-1.5">Past Due</span>
                    </div>
                  }
                  @if (item.open) {
                    <div class="tw-flex tw-items-center tw-pb-0.5">
                      <app-tag
                        tagStyle="Left"
                        type="Info"
                        class="tw-pl-1"
                        width="30px"
                      >
                        {{ item.open }}</app-tag
                      >
                      <span class="tw-font-boldx tw-px-1.5">Open</span>
                    </div>
                  }
                  @if (item.draft) {
                    <div class="tw-flex tw-items-center tw-pb-0.5">
                      <app-tag
                        tagStyle="Left"
                        type="Warn"
                        class="tw-pl-1"
                        width="30px"
                      >
                        {{ item.draft }}</app-tag
                      >
                      <span class="tw-font-boldx tw-px-1.5">Draft</span>
                    </div>
                  }

                  <div class="tw-grow"></div>
                </div>
              </div>
            }
          </div>
        </ng-template>
      </app-loading-panel>
    </div>
  `,
  styles: `
    .week-header {
      font-size: 14px;
      font-family: var(--sed-header-font-family);
    }

    .date-span {
      font-size: 15px;
      color: black;
      width: 170px;
      display: inline-block;
      text-align: center;
    }
  `,
})
export class TaskWeekComponent extends BaseComponent implements OnInit {
  @Output() includeCompletedChange = new EventEmitter<boolean>();
  @Output() calenderAction = new EventEmitter<{
    action: string;
    weekStartDate: Date;
    weekEndDate: Date;
    claimSpecificFields?: any;
  }>();

  @Input() items: any[] = [];
  @Input() claim: any = null;
  @Input() isPageTodo = false;

  @Input()
  loading = false;

  control: FormControl = new FormControl();

  weekStartDate: Date;
  weekEndDate: Date;
  formattedWeekRange: string;

  taskResponseData: TaskResponse[];
  taskRequest: TaskRequest;
  CurrentValue: any;

  async ngOnInit() {
    this.includeCompletedChange.emit(this.control.value);
    this.dispose.subs = this.control.valueChanges.subscribe(
      (includeCompleted: boolean) => {
        this.includeCompletedChange.emit(includeCompleted);
      },
    );
    this.CurrentValue = this.storage.get('date-todo-view');
    if (this.CurrentValue) {
      this.setWeekRange(new Date(this.CurrentValue));
    } else {
      this.setWeekRange(new Date());
    }
    this.calenderAction.emit({
      action: 'init',
      weekStartDate: this.weekStartDate,
      weekEndDate: this.weekEndDate,
      claimSpecificFields: this.getClaimSpecificFields(),
    });
    this.storage.remove('date-todo-view');
  }

  calendarOnClick(item: any) {
    const calenderDate = new Date(item.date);
    this.calenderAction.emit({
      action: 'calendarClick',
      weekStartDate: calenderDate,
      weekEndDate: calenderDate,
      claimSpecificFields: this.getClaimSpecificFields(),
    });
  }

  goToPreviousWeek() {
    const previousWeek = new Date(this.weekStartDate);
    previousWeek.setDate(this.weekStartDate.getDate() - 7);
    this.setWeekRange(previousWeek);
    this.calenderAction.emit({
      action: 'previous',
      weekStartDate: this.weekStartDate,
      weekEndDate: this.weekEndDate,
      claimSpecificFields: this.getClaimSpecificFields(),
    });
  }

  goToNextWeek() {
    const nextWeek = new Date(this.weekStartDate);
    nextWeek.setDate(this.weekStartDate.getDate() + 7);
    this.setWeekRange(nextWeek);
    this.calenderAction.emit({
      action: 'next',
      weekStartDate: this.weekStartDate,
      weekEndDate: this.weekEndDate,
      claimSpecificFields: this.getClaimSpecificFields(),
    });
  }

  goToToday() {
    const today = new Date();
    this.setWeekRange(today);
    this.calenderAction.emit({
      action: 'today',
      weekStartDate: this.weekStartDate,
      weekEndDate: this.weekEndDate,
      claimSpecificFields: this.getClaimSpecificFields(),
    });
  }

  private setWeekRange(baseDate: Date) {
    this.weekStartDate = DateEx.getStartOfWeek(baseDate);
    this.weekEndDate = DateEx.getEndOfWeek(this.weekStartDate);
    this.formattedWeekRange = `${DateEx.formatShortDate(
      this.weekStartDate,
    )} - ${DateEx.formatShortDate(
      this.weekEndDate,
    )}, ${this.weekEndDate.getFullYear()}`;
  }

  private getClaimSpecificFields(): any {
    const claim = this.view.currentClaim();
    const z: TaskRequest = {
      claimUID: null,
      absenceUID: null,
      employeeUID: null,
      adaEventNumber: null,
      leaveNumber: null,
      adaTamsRequestID: null,
    };
    if (!claim) {
      return { ...z };
    }

    switch (claim.recordSource) {
      case 'J':
        return { ...z, claimUID: claim.recordId };
      case 'T':
        return {
          ...z,
          absenceUID: claim.fileNumber,
          employeeUID: claim.hrMasterUid,
        };
      case 'A':
        return { ...z, adaEventNumber: numericAdaOrLeaveKeyFromClaim(claim) };
      case 'L':
        return { ...z, leaveNumber: numericAdaOrLeaveKeyFromClaim(claim) };
      case 'R':
        return { ...z, adaTamsRequestID: claim.recordId };
      default:
        return { ...z, claimUID: claim.recordId };
    }
  }
}
